import * as genericService from "./genericFormServices";
import type { LegalAdviceFormEightData } from "../types/LegalAdvices/LegalAdviceFormEight.type";
import { formatSignatureUrl, type FetchParams } from "./NetworkService";
import type { CreateAttachmentDto } from "../types/MainType.type";

const ENDPOINT = "legal-advice-form-eight";

export interface LegalAdviceFetchResponse {
  data: LegalAdviceFormEightData[];
  totalItems: number;
}

// ---------------------------------------------------------------------------
// 1. Mapper for Legal Advice Form Eight
// ---------------------------------------------------------------------------
export const mapLegalAdviceBackendToFrontend = (
  item: any
): LegalAdviceFormEightData => {
  if (!item) return {} as LegalAdviceFormEightData;

  // Extract inner form values safely
  const form =
    item.legalAdviceFormEight ||
    item.legalAdviceForm ||
    item.formData ||
    {};

  return {
    // Document Level Identifiers
    id: item.id || form.documentId || form.id || '',
    documentNumber: item.documentNumber || '',
    formType: item.formType || 'LEGAL_ADVICE_FORM_EIGHT',
    status: item.status || form.status || 'DRAFT',

    // Form Eight Specific Fields
    // office: form.office || "",
    // regionOrState: form.regionOrState || "",
    scrutinyCaseNo: form.scrutinyCaseNo || "",
    scrutinyRegisterNo: form.scrutinyRegisterNo || "",
    submitterNameOrOffice: form.submitterNameOrOffice || "",
    acquittalCourtDetails: form.acquittalCourtDetails || "",
    prosecutedLawSection: form.prosecutedLawSection || "",
    chargedLawSection: form.chargedLawSection || "",
    limitationExpiryDate: form.limitationExpiryDate || "",
    plaintiffArgumentAnalysis: form.plaintiffArgumentAnalysis || "",
    defendantArgumentAnalysis: form.defendantArgumentAnalysis || "",
    courtJudgmentAnalysis: form.courtJudgmentAnalysis || "",

    // Handling Officer Signatures & Info
    handlingOfficerOpinion: form.handlingOfficerOpinion || "",
    handlingOfficerName: form.handlingOfficerName || "",
    handlingOfficerReceivedDate: form.handlingOfficerReceivedDate || "",
    handlingOfficerSubmitDate: form.handlingOfficerSubmitDate || "",
    handlingOfficerSignature: formatSignatureUrl(form.handlingOfficerSignature),

    // In-Charge Officer Signatures & Info
    inChargeOfficerOpinion: form.inChargeOfficerOpinion || "",
    inChargeOfficerName: form.inChargeOfficerName || "",
    inChargeOfficerReceivedDate: form.inChargeOfficerReceivedDate || "",
    inChargeOfficerSubmitDate: form.inChargeOfficerSubmitDate || "",
    inChargeOfficerSignature: formatSignatureUrl(form.inChargeOfficerSignature),

    regionOfficerOpinion: form.regionOfficerOpinion || "",
    regionOfficerName: form.regionOfficerName || "",
    regionOfficerSubmitDate: form.regionOfficerSubmitDate || "",
    regionOfficerSignature: formatSignatureUrl(form.regionOfficerSignature),

    // Geo Levels
    originGeoLevel: item.originGeoLevel || "",
    targetGeoLevels: Array.isArray(item.targetGeoLevels) ? item.targetGeoLevels : [],

    // Attachments Array
    attachments: item.attachments || form.attachments || [],

    // Dates & Status Metadata
    rejectionReason: item.rejectionReason || form.rejectionReason || '',
    submitDate: form.submitDate || item.signedAt || item.updatedAt || '',
    createdAt: item.createdAt || form.createdAt || '',
    updatedAt: item.updatedAt || form.updatedAt || '',
    submittedAt: item.submittedAt || form.submittedAt || '',

    // Relational Metadata
    createdBy: item.createdBy
      ? {
          id: item.createdBy.id,
          name: item.createdBy.name,
          email: item.createdBy.email,
          role: item.createdBy.role,
          position: item.createdBy.position,
        }
      : undefined,
    originOffice: item.originOffice
      ? {
          id: item.originOffice.id,
          name: item.originOffice.name,
          code: item.originOffice.code,
        }
      : undefined,
    stageHistory: Array.isArray(item.stageHistory)
      ? item.stageHistory.map((history: any) => ({
          id: history.id,
          action: history.action,
          comments: history.comments,
          attachments: history.attachments,
          createdAt: history.createdAt,
          performedBy: history.performedBy
            ? {
                id: history.performedBy.id,
                name: history.performedBy.name,
                role: history.performedBy.role,
                position: history.performedBy.position,
                office: history.performedBy.office
                  ? {
                      id: history.performedBy.office.id,
                      name: history.performedBy.office.name,
                      code: history.performedBy.office.code,
                      level: history.performedBy.office.level,
                    }
                  : undefined,
              }
            : undefined,
        }))
      : [],
    auditLogs: Array.isArray(item.auditLogs) ? item.auditLogs : [],
  };
};

// ---------------------------------------------------------------------------
// 2. Custom Filter Function for Legal Advice Form Eight
// ---------------------------------------------------------------------------
const legalAdviceFilterPredicate = (
  app: LegalAdviceFormEightData,
  params: FetchParams
): boolean => {
  const { searchTerm, selectedStatus, selectedDate } = params;

  const matchesStatus = !selectedStatus || app.status === selectedStatus;

  // Form Eight Date Matching
  const formattedAppDate = app.submitDate || app.createdAt || '';

  const matchesDate = !selectedDate || Boolean(formattedAppDate.includes(selectedDate));

  const term = (searchTerm || "").toLowerCase().trim();

  // Search matching across Form Eight fields
  const matchesGeneralSearch =
    !term ||
    Boolean(app.documentNumber && app.documentNumber.toLowerCase().includes(term)) ||
    Boolean(app.scrutinyCaseNo && app.scrutinyCaseNo.toLowerCase().includes(term)) ||
    Boolean(app.scrutinyRegisterNo && app.scrutinyRegisterNo.toLowerCase().includes(term)) ||
    Boolean(app.submitterNameOrOffice && app.submitterNameOrOffice.toLowerCase().includes(term)) ||
    Boolean(app.prosecutedLawSection && app.prosecutedLawSection.toLowerCase().includes(term)) ||
    Boolean(app.chargedLawSection && app.chargedLawSection.toLowerCase().includes(term)) ||
    Boolean(app.handlingOfficerName && app.handlingOfficerName.toLowerCase().includes(term)) ||
    Boolean(app.inChargeOfficerName && app.inChargeOfficerName.toLowerCase().includes(term));

  return Boolean(matchesStatus && matchesDate && matchesGeneralSearch);
};

// ---------------------------------------------------------------------------
// 3. API Request Functions
// ---------------------------------------------------------------------------

export const fetchLegalAdviceApplications = (
  params: FetchParams
): Promise<LegalAdviceFetchResponse> =>
  genericService.fetchApplications<LegalAdviceFormEightData>(
    ENDPOINT,
    params,
    mapLegalAdviceBackendToFrontend,
    legalAdviceFilterPredicate
  );

export const fetchLegalAdviceById = (
  id: string
): Promise<LegalAdviceFormEightData> =>
  genericService.fetchApplicationById<LegalAdviceFormEightData>(
    ENDPOINT,
    id,
    mapLegalAdviceBackendToFrontend
  );

export const saveLegalAdviceDraft = (
  id: string | null,
  data: Partial<LegalAdviceFormEightData>
) => genericService.saveDraft<LegalAdviceFormEightData>(ENDPOINT, id, data);

export const submitLegalAdviceForm = (
  id: string,
  data: LegalAdviceFormEightData
) => genericService.submitForm<LegalAdviceFormEightData>(ENDPOINT, id, data);

export const deleteLegalAdviceForm = (id: string) =>
  genericService.deleteForm(ENDPOINT, id);

// ---------------------------------------------------------------------------
// 4. Superior Stage Actions
// ---------------------------------------------------------------------------

export const startReviewLegalAdviceForm = (id: string) =>
  genericService.startReviewForm(id);

export const attachLegalAdviceForm = (
  id: string,
  comments?: string,
  attachments?: CreateAttachmentDto[],
  targetGeoLevels?: string[]
) => genericService.attachForm(id, comments, attachments, targetGeoLevels);

export const uploadFileService = (file: File) =>
  genericService.uploadFile(file);